import type { Kpi } from "./kpi";
import { KpiEventType } from "./kpi";
import type {
  BSqmQosErrorEvent,
  DiagnosticLogEvent,
  HealthDimensionsMinified,
  HealthRequestMinified,
  HeartbeatEvent,
  KpiEvent,
  KpiEventError,
  OfflineEvent,
  QosErrorEvent,
  QosPillarEvent
} from "./healthTelemetryEvents";
import {
  convertStringMapToObject,
  getDedupedArray,
  jsonStringify
} from "./utils";
import type {
  AdditionalValues,
  nullable,
  HealthConfig,
  HealthDimensions,
  IHealth
} from "./generated/healthSmall.gen";
import { KpiType } from "./generated/healthSmall.gen";

export const enum EventTypeForLoggerAndOfflineWorker {
  BSqmError = 0,
  Kpi = 1,
  QosError = 2,
  QosPillar = 3,
  DiagnosticLog = 4,
  OfflineWorkerInit = 5,
  OfflineWorkerFlush = 6,
  OfflineWorkerFailedUpload = 7
}

export const enum HealthRequestPayloadComponent {
  HealthDimensions = "d",
  PartCDimensions = "a",
  BSqmErrors = "b",
  QosErrors = "e",
  Heartbeat = "h",
  QosPillars = "q",
  Kpis = "k",
  DiagnosticLogs = "l",
  ClientSendTime = "t"
}

export const enum DiagnosticLogEventIds {
  AttemptToUseFunctionalityBeforeIitialization = 0,
  WorkerApiIsNotAvailable = 1,
  InvalidWorkerUrl = 2,
  InstallingOfflineWorker = 3,
  OfflineWorkerMessage = 4,
  FailedToProcessMessageFromOfflineWorker = 5,
  FailedToInstallOfflineWorker = 6,
  InitializedHealth = 7,
  BatchesOfHealthEventsInIndexedDB = 8,
  IndexedDbTransactionFailed = 9,
  InstalledOfflineWorker = 10,
  IndexedDdIsNotSupported = 11,
  GetAllKeysIsNotSupported = 12,
  FailedToProcessMessage = 13,
  FailedToLoadWorkerScript = 14,
  FailedToSendEventToWorker = 15,
  FailedToJsonStringifyEvent = 16,
  IndexedDbEntryIsNotAnObject = 17
}

const enum InitializationCheckWarningContext {
  AttemptedToAddOrUpdatePartCDimension = 0,
  AttemptedToRecordBSqmQosError = 1,
  AttemptedToRecordSessionEndingQosError = 2,
  AttemptedToSetAppOnlineStatus = 3,
  AttemptedToUpdatePartADimensions = 4
}

const enum SendToOfflineWorkerResult {
  SendSucceeded = 0,
  SendFailed = 1,
  DidNotAttempt = 2
}

// Health
export class Health implements IHealth {
  private _additionalPartCDimensions: {
    [name: string]: string | nullable;
  } = {};
  private _additionalErrorPartCDimensions: {
    [name: string]: string | nullable;
  } = {};
  private _alertableKpiNames: string[] = [];
  private _dimensions: HealthDimensionsMinified = {};
  private _dimensionsVersion: number = 0;
  private _healthConfig: HealthConfigInternal = {
    _heartbeatIntervalMs: 300000,
    _isHealthEnabled: false,
    _isOfflineEnabled: false,
    _isErrorPartCEnabled: false,
    _maxIntervalMs: 30000,
    _maxQueueSize: 25,
    _offlineWorkerUrl: "",
    _pillarIntervalMs: 86400000,
    _telemetryUrl: "",
    _absoluteTelemetryUrl: "",
    _maxConcurrentRetries: 3,
    _retryMaxAttempts: 3,
    _retryDelayMs: 15000,
    _disabledHealthEvents: new Set<string>(),
    _isFetchApiEnabled: false
  };
  private _heartbeatInitialStartCalled: boolean = false;
  private _heartbeatOrderingNumber: number = -1;
  private _heartbeatRunning: boolean = false;
  private _heartbeatSendTimerId: number = 0;
  private _isAppOnline: boolean = true;
  private _isAppShuttingDown: () => boolean = () => false;
  private _kpis: { [name: string]: Kpi } = {};
  private _loggerBSqmQosErrorQueue: BSqmQosErrorEvent[] = [];
  private _loggerCurrentQueueSize: number = 0;
  private _loggerNonHeartbeatDiagnosticLogsOrPillarsQueueSize: number = 0;
  private _loggerQueuedHeartbeatEvent?: HeartbeatEvent = undefined; // Individual heartbeat events can be collapsed down into a single event for bandwidth savings.  No need for a queue of them.
  private _loggerKpiQueue: KpiEvent[] = [];
  private _loggerQosErrorQueue: QosErrorEvent[] = [];
  private _loggerQosPillarQueue: QosPillarEvent[] = [];
  private _loggerDiagnosticLogQueue: DiagnosticLogEvent[] = [];
  private _loggerSendTimerId: number = 0;
  private _offlineWorker: Worker | undefined;
  private _recordPillarLastTimestamp: number = 0;
  private _recordPillarTimerId: number = 0;
  private _sessionPillars: string[] = [];
  private _hasAtLeastOneRequestSucceeded: boolean = false;
  private _currentRetriesCount: number = 0;

  // The only functionality we want to provide before initialization has completed is being able to add Health 2.0 events to our queues
  private _initializationCompleted: boolean = false;

  private static _instance: Health;

  private constructor() {}

  /**
   * Get the singleton instance of Health
   * @param forceNew - Force a new instance of Health to be created. This is useful for testing purposes.
   * @returns - The singleton instance of Health
   */
  public static getInstance(forceNew: boolean = false): Health {
    if (!Health._instance || forceNew) {
      Health._instance = new Health();
    }

    return this._instance;
  }

  public initialize(healthConfig: HealthConfig) {
    if (healthConfig) {
      try {
        this._healthConfig = convertToHealthConfigInternal(healthConfig);
        this._updateMinifiedPartADimensions();
        this._loggerScheduleEventUploadTimer();
        this._startOfflineWorker();
        this._initializationCompleted = true;

        this._diagnosticLog(DiagnosticLogEventIds.InitializedHealth);

        return;
      } catch (_: any) {}
    }

    this._initializationCompleted = false;
  }

  // ------------------------------------------------------------------------
  // Begin: Health Public API Methods
  // ------------------------------------------------------------------------

  // API Method: addOrUpdatePartCDimension --> Attempts to add or update a single part C dimension value
  public addOrUpdatePartCDimension(
    dimension: string | nullable,
    value: string | nullable
  ) {
    if (
      !this._checkForInitialization(
        InitializationCheckWarningContext.AttemptedToAddOrUpdatePartCDimension
      )
    ) {
      // Updating a Part C dimension requires us to send the current queue of events, so can't do that unless we are initialized
      return;
    }

    if (dimension) {
      if (
        !this._additionalPartCDimensions.hasOwnProperty(dimension) ||
        this._additionalPartCDimensions[dimension] !== value
      ) {
        // A dimension is changing so need to send the current events in the queue
        // so they have the correct dimension values as when they were created.
        // Note: we should only send if there are additional events beyond diagnostic logs and/or pillars.
        // If the only thing queued are pillar events, then we shouldn't be sending those
        // out every time a part C dimension is updated.  This helps prevent multiple
        // sets of pillar events going out when multiple part C dimensions are being updated
        // via multiple calls to the api.
        this._loggerSendEventsAndRequeue(
          true /* doNotSendIfOnlyHeartbeatsOrLogsAreQueued */
        );

        this._additionalPartCDimensions[dimension] = value;
        this._dimensionsVersion += 1;

        // Log out an updated heartbeat
        this._logHeartbeatEvent();

        // Re-log our session pillars to make sure they get logged with the updated dimensions
        this._logAllPillars();
      }
    }
  }

  // API Method: addOrUpdateErrorPartCDimension --> Attempts to add or update a single part C dimension value, which will be only send with error events
  public addOrUpdateErrorPartCDimension(
    dimension: string | nullable,
    value: string | nullable
  ) {
    if (!this._healthConfig._isErrorPartCEnabled) {
      return;
    }

    if (!!dimension) {
      this._additionalErrorPartCDimensions[dimension] = value;
    }
  }

  // API Method: addQosPillar --> Add pillar
  public addQosPillar(pillarName: string | nullable) {
    if (pillarName) {
      const veryFirstPillar: boolean = this._sessionPillars.length === 0;

      if (this._sessionPillars.indexOf(pillarName) === -1) {
        this._sessionPillars.push(pillarName);

        this._logPillar(pillarName, false /* loggingFullPillarsSet */);
        this._pillarsChanged(veryFirstPillar);
      }
    }
  }

  // API Method: recordBsqmQosError --> Record a bSqm-based QoS error
  public recordBsqmQosError(
    bSqmErrorId: string | nullable,
    additionalVetoes?: (string | nullable)[] | nullable,
    additionalValues?: AdditionalValues | nullable
  ) {
    if (
      !this._checkForInitialization(
        InitializationCheckWarningContext.AttemptedToRecordBSqmQosError
      )
    ) {
      // A bSqm error is a session-ending error so can't log one unless we've initialized Health
      return;
    }

    // BSqm errors are all traditionally session ending errors, so need to shut things down when encountered
    this._stopAllHealthTimers(false /* flushEvents */);

    // Re-log our session pillars to make sure we have this session in the denominator before veto-ing it
    this._logAllPillars();

    if (bSqmErrorId) {
      const minifiedEvent: { [name: string]: any } = {
        b: bSqmErrorId,
        v: additionalVetoes,
        d: additionalValues?.durationMs,
        p: convertStringMapToObject(additionalValues?.extendedProperties),
        s: this._sessionPillars,
        i: additionalValues?.inStaging
      };

      this._loggerQueueEvent(
        EventTypeForLoggerAndOfflineWorker.BSqmError,
        minifiedEvent,
        bSqmErrorId
      );

      // If we are offline, even if this is a session-ending error,
      // we don't want to shutdown the offline worker so that it can
      // keep running the offline support
      this.shutdown(true /* doNotTerminateWorkerIfOffline */);
    }
  }

  // API Method: recordKpiFailure --> Record a kpi failure
  public recordKpiFailure(
    kpiName: string | nullable,
    errorName?: string | nullable,
    isIntentional?: boolean | nullable,
    isInternal?: boolean | nullable,
    additionalValues?: AdditionalValues | nullable
  ) {
    this._logKpiEvent(
      kpiName,
      true /* kpiMustAlreadyExist */,
      KpiEventType.Failure,
      additionalValues,
      errorName,
      isInternal,
      isIntentional
    );
  }

  // API Method: recordKpiImpliedFailure --> Record a kpi failure, where the usage is implied
  public recordKpiImpliedFailure(
    kpiName: string | nullable,
    errorName?: string | nullable,
    isIntentional?: boolean | nullable,
    isInternal?: boolean | nullable,
    owningFeatureCrewAlias?: string | nullable,
    additionalValues?: AdditionalValues | nullable
  ) {
    this._logKpiEvent(
      kpiName,
      false /* kpiMustAlreadyExist */,
      KpiEventType.ImpliedUsageFailure,
      additionalValues,
      errorName,
      isInternal,
      isIntentional,
      KpiType.FailureBased /* kpiTypeIfNeedToInit */,
      false /* isAlertableIfNeedToInit */,
      owningFeatureCrewAlias /* owningFeatureCrewAliasIfNeedToInit */
    );
  }

  // API Method: recordKpiImpliedSuccess --> Record a kpi success, where the usage is implied
  public recordKpiImpliedSuccess(
    kpiName: string | nullable,
    owningFeatureCrewAlias?: string | nullable,
    additionalValues?: AdditionalValues | nullable
  ) {
    this._logKpiEvent(
      kpiName,
      false /* kpiMustAlreadyExist */,
      KpiEventType.ImpliedUsageSuccess,
      additionalValues,
      undefined /* kpiErrorName */,
      undefined /* isInternal */,
      undefined /* isIntentional */,
      KpiType.FailureBased /* kpiTypeIfNeedToInit */,
      false /* isAlertableIfNeedToInit */,
      owningFeatureCrewAlias /* owningFeatureCrewAliasIfNeedToInit */
    );
  }

  // API Method: recordKpiSuccess --> Record a kpi success
  public recordKpiSuccess(
    kpiName: string | nullable,
    additionalValues?: AdditionalValues | nullable
  ) {
    this._logKpiEvent(
      kpiName,
      true /* kpiMustAlreadyExist */,
      KpiEventType.Success,
      additionalValues
    );
  }

  // API Method: recordKpiUsage --> Record a kpi usage
  public recordKpiUsage(
    kpiName: string | nullable,
    kpiType: KpiType,
    owningFeatureCrewAlias?: string | nullable,
    extendedProperties?: Map<string | nullable, string | nullable> | nullable
  ) {
    this._logKpiEvent(
      kpiName,
      false /* kpiMustAlreadyExist */,
      KpiEventType.Usage,
      {
        extendedProperties
      } /* additionalValues */,
      undefined /* kpiErrorName */,
      undefined /* isInternal */,
      undefined /* isIntentional */,
      kpiType /* kpiTypeIfNeedToInit */,
      false /* isAlertableIfNeedToInit */,
      owningFeatureCrewAlias /* owningFeatureCrewAliasIfNeedToInit */
    );
  }

  // API Method: recordKpiUsageForAlertableKpi --> Record a kpi usage for an alertable kpi
  public recordKpiUsageForAlertableKpi(
    kpiName: string | nullable,
    owningFeatureCrewAlias?: string | nullable,
    extendedProperties?: Map<string | nullable, string | nullable> | nullable
  ) {
    if (!kpiName) {
      return;
    }
    // Need to check that the kpi wasn't already started as a SuccessBased kpi,
    // because SuccessBased kpis can't be alertable
    let kpiCanBeAlertable = true;
    const kpi = this._kpis[kpiName];
    if (kpi) kpiCanBeAlertable = kpi.t === KpiType.FailureBased;

    this._logKpiEvent(
      kpiName,
      false /* kpiMustAlreadyExist */,
      KpiEventType.Usage,
      {
        extendedProperties
      } /* additionalValues */,
      undefined /* kpiErrorName */,
      undefined /* isInternal */,
      undefined /* isIntentional */,
      KpiType.FailureBased /* kpiTypeIfNeedToInit */,
      true /* isAlertableIfNeedToInit */,
      owningFeatureCrewAlias /* owningFeatureCrewAliasIfNeedToInit */
    );

    if (kpiCanBeAlertable) {
      // Ensure the heartbeat is started because alertable kpis need a heartbeat
      this._startHeartbeatIfNotAlreadyStarted();
    }
  }

  public recordQosErrorForCopilot(
    copilotInteractionId: string | nullable,
    errorName: string | nullable,
    vetoes: (string | nullable)[] | nullable,
    isIntentional: boolean | nullable,
    isInternal: boolean | nullable,
    alertOnPillar: boolean | nullable,
    isSessionEndingError: boolean | nullable,
    additionalValues?: AdditionalValues | nullable
  ) {
    const _additionalValues = additionalValues ? { ...additionalValues } : {};
    _additionalValues.extendedProperties = _additionalValues.extendedProperties
      ? { ..._additionalValues.extendedProperties }
      : new Map<string | nullable, string | nullable>();
    _additionalValues.extendedProperties.set(
      "Copilot.InteractionId",
      copilotInteractionId
    );
    this.recordQosError(
      errorName,
      vetoes,
      isIntentional,
      isInternal,
      alertOnPillar,
      isSessionEndingError,
      _additionalValues
    );
  }

  // API Method: recordQosError --> Record a qos error
  public recordQosError(
    errorName: string | nullable,
    vetoes: (string | nullable)[] | nullable,
    isIntentional: boolean | nullable,
    isInternal: boolean | nullable,
    alertOnPillar: boolean | nullable,
    isSessionEndingError: boolean | nullable,
    additionalValues?: AdditionalValues | nullable
  ) {
    if (
      isSessionEndingError &&
      !this._checkForInitialization(
        InitializationCheckWarningContext.AttemptedToRecordSessionEndingQosError
      )
    ) {
      // A QoS session-ending error can't be logged unless we've initialized Health, because we have to immediately send events and shut down Health
      return;
    }

    if (errorName && vetoes) {
      const minifiedEvent: { [name: string]: any } = {
        n: errorName,
        x: isIntentional,
        z: isInternal,
        l: alertOnPillar,
        v: vetoes,
        e: isSessionEndingError,
        d: additionalValues?.durationMs,
        p: convertStringMapToObject(additionalValues?.extendedProperties),
        s: this._sessionPillars,
        i: additionalValues?.inStaging
      };

      this._loggerQueueEvent(
        EventTypeForLoggerAndOfflineWorker.QosError,
        minifiedEvent,
        errorName
      );

      // Stop the heartbeat and recurring pillar events if this is a session-ending error
      if (isSessionEndingError) {
        this._stopAllHealthTimers(false /* flushEvents */);

        // Re-log our session pillars to make sure we have this session in the denominator before veto-ing it
        this._logAllPillars();

        // If we are offline, even if this is a session-ending error,
        // we don't want to shutdown the offline worker so that it can
        // keep running the offline support
        this.shutdown(true /* doNotTerminateWorkerIfOffline */);
      }
    }
  }

  // API Method: removeQosPillar --> Remove pillar
  public removeQosPillar(pillarName: string | nullable) {
    if (!pillarName) {
      return;
    }

    const pillarIndex = this._sessionPillars.indexOf(pillarName);
    if (pillarIndex > -1) {
      this._sessionPillars.splice(pillarIndex, 1);
      this._pillarsChanged(false);
    }

    // Stop the periodic pillar recording if we've removed all pillars
    if (this._sessionPillars.length === 0) {
      this._stopPeriodicPillarEvents();
    }
  }

  // API Method: resumeAllHealthTimers --> If timers were previously stopped, resumes the timers
  public resumeAllHealthTimers() {
    // Heartbeat
    if (this._heartbeatInitialStartCalled && !this._heartbeatRunning) {
      this._logHeartbeatAndReQueue();
    }

    // Pillar events
    if (
      this._recordPillarTimerId === 0 &&
      this._recordPillarLastTimestamp !== 0
    ) {
      const timeSinceRecordPillarMs =
        Date.now() - this._recordPillarLastTimestamp;

      // If at least `this._recordPillarIntervalMs` ms have passed since we last logged the pillars, or
      // if `Date.now()` is somehow older than or equal to `this._recordPillarLastTimestamp`,
      // log all pillars now without a delay and resume the timer
      if (
        timeSinceRecordPillarMs >= this._healthConfig._pillarIntervalMs ||
        timeSinceRecordPillarMs <= 0
      ) {
        this._logPillarsAndReQueue();
      } else {
        this._queueLogPillars(
          this._healthConfig._pillarIntervalMs - timeSinceRecordPillarMs
        );
      }
    }
  }

  // API Method: setHandlerForIsAppShuttingDown --> Set a callback we can use to tell whether the app is shutting down
  public setHandlerForIsAppShuttingDown(
    isAppShuttingDown: (() => boolean) | nullable
  ) {
    if (isAppShuttingDown) {
      this._isAppShuttingDown = isAppShuttingDown;
    }
  }

  // API Method: setIsAppOnline --> Set the app's online vs. offline status
  public setIsAppOnline(isAppOnline: boolean) {
    if (
      !this._checkForInitialization(
        InitializationCheckWarningContext.AttemptedToSetAppOnlineStatus
      )
    ) {
      // Can't send events if not initialized
      return;
    }

    // Attempt to send any outstanding events if the app just regained connection
    if (!this._isAppOnline && isAppOnline) {
      this._offlineWorker?.postMessage(<OfflineEvent>{
        t: EventTypeForLoggerAndOfflineWorker.OfflineWorkerFlush,
        v: this._isAppShuttingDown()
      });
    }

    this._isAppOnline = isAppOnline;
  }

  // API Method: shutdown --> Shuts the Health system down
  public shutdown(doNotTerminateWorkerIfOffline: boolean = false) {
    if (!this._healthConfig._isHealthEnabled) {
      return;
    }

    this._loggerCancelEventUploadTimer();
    this._stopAllHealthTimers(false /* flushEvents */);
    this._isAppShuttingDown = () => true;
    this._loggerFlush();

    // If we're offline, then there are scenarios where we don't want
    // to shutdown the offline worker so that it can continue to store
    // offline events.  One such scenario is when we hit a session-ending
    // error.  It is important that we don't terminate the offline worker
    // when offline and a session-ending error is encountered so that the
    // health system is still able to record the error in offline storage
    // for later reporting.  Otherwise, we will lose that error in telemetry.
    if (
      !doNotTerminateWorkerIfOffline ||
      (doNotTerminateWorkerIfOffline && this._isAppOnline)
    ) {
      this._offlineWorker?.terminate();
    }
  }

  // API Method: stopAllHealthTimers --> Stop health timers, if they are currently running
  public stopAllHealthTimers() {
    // Flush events after we stop all timers to avoid events from getting stuck in the queues
    this._stopAllHealthTimers(true /* flushEvents */);
  }

  private _stopAllHealthTimers(flushEvents: boolean) {
    // Heartbeat
    this._heartbeatRunning = false;
    this._heartbeatOrderingNumber = -1;

    if (this._heartbeatSendTimerId !== 0) {
      window.clearTimeout(this._heartbeatSendTimerId);
      this._heartbeatSendTimerId = 0;
    }

    // Pillar events
    this._stopPeriodicPillarEvents();

    if (flushEvents) {
      this._loggerFlush();
    }
  }

  // API Method: updatePartADimensions --> Updates Part A dimension(s)
  public updatePartADimensions(dimensions: HealthDimensions | nullable) {
    if (
      !dimensions ||
      !this._checkForInitialization(
        InitializationCheckWarningContext.AttemptedToUpdatePartADimensions
      )
    ) {
      // Updating a Part A dimension requires us to have been initialized
      return;
    }

    let partAChanged: boolean = false;

    // Check for dimension changes and update only if needed.
    // Specifically not allowing a dimension to be "reset" back to an undefined value or an empty string.
    // Note that the value of a dimension can be a boolean so need to explicitly check for undefined/null.
    Object.keys(dimensions).forEach((key) => {
      if (
        (<any>dimensions)[key] !== undefined &&
        (<any>dimensions)[key] !== null &&
        (<any>dimensions)[key] !== "" &&
        (<any>this._healthConfig._dimensions)[key] !== (<any>dimensions)[key]
      ) {
        (<any>this._healthConfig._dimensions)[key] = (<any>dimensions)[key];
        partAChanged = true;
      }
    });

    if (partAChanged) {
      // One or more dimensions have changed so need to send the current events in the queue
      // so they have the correct dimension values as when they were created
      this._loggerSendEventsAndRequeue();

      this._updateMinifiedPartADimensions();
      this._dimensionsVersion += 1;

      // Log out an updated heartbeat
      this._logHeartbeatEvent();

      // Re-log our session pillars to make sure they get logged with the updated dimensions
      this._logAllPillars();
    }
  }

  // API Method: flushAndSetNewEndpoint --> Flushes the queue and sets a new endpoint, such as a data boundary-specific endpoint
  public flushAndSetNewEndpoint(
    remoteTelemetryUrl: string,
    onComplete?: () => void
  ): void {
    if (
      remoteTelemetryUrl.localeCompare(
        this._healthConfig._telemetryUrl ?? "",
        undefined,
        { sensitivity: "accent" }
      )
    ) {
      this._loggerSendEventsAndRequeue(false /* flush all events */);
      this._healthConfig._telemetryUrl = remoteTelemetryUrl;

      if (onComplete) {
        onComplete();
      }
    }
  }

  // ------------------------------------------------------------------------
  // End: Health Public API Methods
  // ------------------------------------------------------------------------

  // ------------------------------------------------------------------------
  // Begin: Health Telemetry Logger (private)
  // ------------------------------------------------------------------------

  private readonly _loggerCancelEventUploadTimer = () => {
    if (this._loggerSendTimerId) {
      window.clearTimeout(this._loggerSendTimerId);
      this._loggerSendTimerId = 0;
    }
  };

  private readonly _loggerEmptyQueues = () => {
    this._loggerBSqmQosErrorQueue = [];
    this._loggerKpiQueue = [];
    this._loggerQosErrorQueue = [];
    this._loggerQueuedHeartbeatEvent = undefined;
    this._loggerQosPillarQueue = [];
    this._loggerDiagnosticLogQueue = [];
    this._loggerCurrentQueueSize = 0;
    this._loggerNonHeartbeatDiagnosticLogsOrPillarsQueueSize = 0;
  };

  private readonly _loggerFlush = () => {
    try {
      if (this._loggerCurrentQueueSize <= 0) {
        return;
      }

      const payload: HealthRequestMinified = this._loggerFormatPayload();
      if (!this._healthConfig._isFetchApiEnabled) {
        this._tryUpload(
          payload,
          this._dimensionsVersion,
          this._healthConfig._retryMaxAttempts
        );
      } else {
        this._tryUpload_fetchApi(
          payload,
          this._dimensionsVersion,
          this._healthConfig._retryMaxAttempts
        );
      }
      this._loggerEmptyQueues();
    } catch (_: any) {
      // Just eat any exception. We don't want a telemetry failure to cause any end-user impacting issue
    }
  };

  private readonly _loggerFormatPayload = (): HealthRequestMinified => {
    const payload: { [name: string]: any } = {
      d: this._dimensions,
      a: this._getMergedPartC(),
      b: this._loggerBSqmQosErrorQueue,
      e: this._loggerQosErrorQueue,
      h: this._loggerQueuedHeartbeatEvent,
      q: this._loggerQosPillarQueue,
      k: this._loggerKpiQueue,
      l: this._loggerDiagnosticLogQueue,
      type: "h_v2"
    };

    return <HealthRequestMinified>payload;
  };

  private readonly _getMergedPartC = () => {
    if (!this._healthConfig._isErrorPartCEnabled) {
      return this._additionalPartCDimensions;
    }

    // only merge error part if we are reporting errors
    const includeErrorDimensions =
      (!!this._loggerBSqmQosErrorQueue &&
        this._loggerBSqmQosErrorQueue.length > 0) ||
      (!!this._loggerQosErrorQueue && this._loggerQosErrorQueue.length > 0);

    return includeErrorDimensions &&
      Object.keys(this._additionalErrorPartCDimensions).length > 0
      ? {
          ...this._additionalPartCDimensions,
          ...this._additionalErrorPartCDimensions
        }
      : this._additionalPartCDimensions;
  };

  private readonly _loggerQueueEvent = (
    eventTypeForLogger: EventTypeForLoggerAndOfflineWorker,
    minifiedEvent: { [name: string]: any },
    eventName: string,
    loggingFullPillarsSet: boolean = false
  ) => {
    if (
      !minifiedEvent ||
      this._healthConfig._disabledHealthEvents.has(eventName)
    ) {
      return;
    }
    if (this._isAppOnline) {
      switch (eventTypeForLogger) {
        case EventTypeForLoggerAndOfflineWorker.BSqmError:
          this._loggerBSqmQosErrorQueue.push(<BSqmQosErrorEvent>minifiedEvent);
          break;

        case EventTypeForLoggerAndOfflineWorker.Kpi:
          this._loggerKpiQueue.push(<KpiEvent>minifiedEvent);
          break;

        case EventTypeForLoggerAndOfflineWorker.QosError:
          this._loggerQosErrorQueue.push(<QosErrorEvent>minifiedEvent);
          break;

        case EventTypeForLoggerAndOfflineWorker.QosPillar:
          // Don't need to add duplicate pillar events, so check before adding to reduce payload size
          if (
            this._loggerQosPillarQueue
              .map((p) => p.n)
              .indexOf((<QosPillarEvent>minifiedEvent).n) === -1
          ) {
            this._loggerQosPillarQueue.push(<QosPillarEvent>minifiedEvent);
          }
          break;

        case EventTypeForLoggerAndOfflineWorker.DiagnosticLog:
          this._loggerDiagnosticLogQueue.push(
            <DiagnosticLogEvent>minifiedEvent
          );
          break;
      }

      this._loggerCurrentQueueSize += 1;
      if (
        eventTypeForLogger !==
          EventTypeForLoggerAndOfflineWorker.DiagnosticLog &&
        eventTypeForLogger !== EventTypeForLoggerAndOfflineWorker.QosPillar
      ) {
        this._loggerNonHeartbeatDiagnosticLogsOrPillarsQueueSize += 1;
      }

      // If the app is shutting down, send any new events immediately
      // We already flush our queue within this.shutdown(), but if anything gets logged after that,
      // we want to make sure we send it to RemoteTelemetry as well
      if (this._isAppShuttingDown() && !loggingFullPillarsSet) {
        this._loggerFlush();
        return;
      }

      if (this._loggerCurrentQueueSize >= this._healthConfig._maxQueueSize) {
        this._loggerCancelEventUploadTimer();
        this._loggerSendEventsAndRequeue();
      }
    } else {
      // Offline
      // Used for Data_IsOffline and Data_OriginalEventTime
      minifiedEvent["o"] = new Date().toJSON();

      this._offlineWorker?.postMessage(<OfflineEvent>{
        t: eventTypeForLogger,
        // Session_Id + dimensions version
        k: `${this._dimensions.w}.${this._dimensionsVersion}`,
        m: this._dimensions,
        c: this._getMergedPartC(),
        v: minifiedEvent
      });
    }
  };

  private readonly _loggerScheduleEventUploadTimer = () => {
    this._loggerCancelEventUploadTimer();

    this._loggerSendTimerId = window.setTimeout(() => {
      this._loggerSendEventsAndRequeue();
    }, this._healthConfig._maxIntervalMs);
  };

  private readonly _loggerSendEventsAndRequeue = (
    doNotSendIfOnlyHeartbeatsOrLogsAreQueued: boolean = false
  ) => {
    // Heartbeats don't use Part C, so don't need to send events if only part C was updated.
    // If the only thing queued are pillar events, then we shouldn't be sending those
    // out every time a part C dimension is updated.  This helps prevent multiple
    // sets of pillar events going out when multiple part C dimensions are being updated
    // via multiple calls to the api.
    if (
      doNotSendIfOnlyHeartbeatsOrLogsAreQueued &&
      this._loggerNonHeartbeatDiagnosticLogsOrPillarsQueueSize <= 0
    ) {
      return;
    }

    this._loggerFlush();
    this._loggerScheduleEventUploadTimer();
  };

  private readonly _logHeartbeatEvent = () => {
    if (!this._sessionPillars && !this._kpis) {
      return;
    }

    // Increment ordering number if there is no currently queued event or if the ordering number is -1
    if (
      !this._loggerQueuedHeartbeatEvent ||
      this._heartbeatOrderingNumber === -1
    ) {
      this._heartbeatOrderingNumber =
        (this._heartbeatOrderingNumber + 1) % 1000;
    }

    // There is only a single heartbeat event in the queue at a time,
    // so if a heartbeat event already exists in the queue, then don't bump the queue size
    if (!this._loggerQueuedHeartbeatEvent) {
      this._loggerCurrentQueueSize += 1;
    }

    // If there already exists a queued heartbeat event, then just merge in anything new to that existing event, de-duping.
    const minifiedEvent: HeartbeatEvent = {
      p: this._loggerQueuedHeartbeatEvent
        ? getDedupedArray(
            this._loggerQueuedHeartbeatEvent.p,
            this._sessionPillars
          )
        : this._sessionPillars,
      k: this._loggerQueuedHeartbeatEvent
        ? getDedupedArray(
            this._loggerQueuedHeartbeatEvent.k,
            this._alertableKpiNames
          )
        : this._alertableKpiNames,
      i: this._healthConfig._heartbeatIntervalMs,
      o: this._heartbeatOrderingNumber
    };

    this._loggerQueuedHeartbeatEvent = minifiedEvent;
  };

  private readonly _logKpiEvent = (
    kpiName: string | nullable,
    kpiMustAlreadyExist: boolean,
    kpiEvent: KpiEventType,
    additionalValues?: AdditionalValues | nullable,
    kpiErrorName?: string | nullable,
    isInternal?: boolean | nullable,
    isIntentional?: boolean | nullable,
    kpiTypeIfNeedToInit?: KpiType,
    isAlertableIfNeedToInit?: boolean,
    owningFeatureCrewAliasIfNeedToInit?: string | nullable
  ) => {
    if (!kpiName || (kpiMustAlreadyExist && !this._kpis[kpiName])) {
      return;
    }

    if (!kpiMustAlreadyExist) {
      this._initKpiIfNeeded(
        kpiName,
        kpiTypeIfNeedToInit,
        isAlertableIfNeedToInit,
        owningFeatureCrewAliasIfNeedToInit
      );
    }

    const kpi = this._kpis[kpiName];

    const minifiedEvent: { [name: string]: any } = {
      n: kpi.k,
      e: kpiEvent,
      t: kpi.t,
      a: kpi.a,
      f: kpi.o,
      d: additionalValues?.durationMs,
      p: convertStringMapToObject(additionalValues?.extendedProperties)
    };

    if (kpiErrorName) {
      const minifiedKpiError: { [name: string]: any } = {
        n: kpiErrorName,
        x: isIntentional,
        z: isInternal
      };

      minifiedEvent["k"] = <KpiEventError>minifiedKpiError;
    }

    this._loggerQueueEvent(
      EventTypeForLoggerAndOfflineWorker.Kpi,
      minifiedEvent,
      kpiName
    );
  };

  private readonly _updateMinifiedPartADimensions = () => {
    // local variable that will be minimized in size for calls below (package size optimization)
    const dimensions: HealthDimensions | undefined =
      this._healthConfig._dimensions;
    if (dimensions) {
      this._dimensions = {
        a: dimensions.application ?? undefined,
        b: dimensions.applicationLcid ?? undefined,
        c: dimensions.applicationMode ?? undefined,
        d: dimensions.applicationModeExtended ?? undefined,
        e: dimensions.arrVersion ?? undefined,
        f: dimensions.browser ?? undefined,
        g: dimensions.browserLcid ?? undefined,
        h: dimensions.browserMajorVersion ?? undefined,
        i: dimensions.browserVersion ?? undefined,
        j: dimensions.clientVersion ?? undefined,
        k: dimensions.datacenter ?? undefined,
        l: dimensions.dataCulture ?? undefined,
        m: dimensions.flightsImpressionId ?? undefined,
        n: dimensions.host ?? undefined,
        o: dimensions.isAnonymousUser ?? undefined,
        p: dimensions.isBusinessUser ?? undefined,
        q: dimensions.isEduUser ?? undefined,
        r: dimensions.isSynthetic ?? undefined,
        s: dimensions.loggableUserId ?? undefined,
        t: dimensions.loggableUserIdSpace ?? undefined,
        u: dimensions.ring ?? undefined,
        v: dimensions.serverDocId ?? undefined,
        w: dimensions.session_id ?? undefined,
        x: dimensions.sessionOrigin ?? undefined,
        y: dimensions.tenantId ?? undefined,
        z: dimensions.uiHost ?? undefined,
        aa: dimensions.wfeVersion ?? undefined,
        ab: dimensions.fileExtension ?? undefined,
        ac: dimensions.uiHostIntegrationType ?? undefined,
        ad: dimensions.osVersion ?? undefined,
        af: dimensions.networkDownlink ?? undefined,
        ag: dimensions.networkDownlinkMax ?? undefined,
        ah: dimensions.networkEffectiveType ?? undefined,
        ai: dimensions.networkRTT ?? undefined,
        aj: dimensions.networkSaveData ?? undefined,
        ak: dimensions.networkType ?? undefined,
        al: dimensions.userLicenseType ?? undefined
      };
    }
  };

  // ------------------------------------------------------------------------
  // End: Health Telemetry Logger (private)
  // ------------------------------------------------------------------------

  // ------------------------------------------------------------------------
  // Begin: Timers (private)
  // ------------------------------------------------------------------------

  private readonly _logHeartbeatAndReQueue = () => {
    this._logHeartbeatEvent();

    // Queue up the next heartbeat
    this._heartbeatSendTimerId = window.setTimeout(() => {
      this._logHeartbeatAndReQueue();
    }, this._healthConfig._heartbeatIntervalMs);
  };

  private readonly _logAllPillars = () => {
    if (!this._sessionPillars) {
      return;
    }

    this._sessionPillars.forEach((pillarName) => {
      if (pillarName) {
        this._logPillar(pillarName, true /* loggingFullPillarsSet */);
      }
    });
  };

  private readonly _logPillar = (
    pillarName: string,
    loggingFullPillarsSet: boolean
  ) => {
    const minifiedEvent: { [name: string]: any } = {};
    minifiedEvent["n"] = pillarName;

    this._loggerQueueEvent(
      EventTypeForLoggerAndOfflineWorker.QosPillar,
      minifiedEvent,
      pillarName,
      loggingFullPillarsSet
    );
  };

  private readonly _logPillarsAndReQueue = () => {
    if (this._sessionPillars) {
      this._recordPillarLastTimestamp = Date.now();

      this._logAllPillars();
    }

    this._queueLogPillars();
  };

  private readonly _queueLogPillars = (
    delay: number = this._healthConfig._pillarIntervalMs
  ) => {
    this._recordPillarTimerId = window.setTimeout(() => {
      this._logPillarsAndReQueue();
    }, delay);
  };

  private readonly _startHeartbeatIfNotAlreadyStarted = () => {
    // Heartbeat
    if (!this._heartbeatInitialStartCalled) {
      this._heartbeatInitialStartCalled = true;
      this._heartbeatRunning = true;

      // When the heartbeat is first started, immediately send one
      this._logHeartbeatAndReQueue();
    }
  };

  private readonly _stopPeriodicPillarEvents = () => {
    if (this._recordPillarTimerId !== 0) {
      window.clearTimeout(this._recordPillarTimerId);
      this._recordPillarTimerId = 0;
    }
  };

  // ------------------------------------------------------------------------
  // End: Timers (private)
  // ------------------------------------------------------------------------

  // ------------------------------------------------------------------------
  // Begin: Offline Support (private)
  // ------------------------------------------------------------------------

  private readonly _startOfflineWorker = () => {
    if (
      !this._healthConfig._isHealthEnabled ||
      !this._healthConfig._isOfflineEnabled ||
      !this._healthConfig._absoluteTelemetryUrl ||
      this._healthConfig._absoluteTelemetryUrl === "" ||
      !this._healthConfig._offlineWorkerUrl ||
      this._healthConfig._offlineWorkerUrl === ""
    ) {
      return;
    }

    try {
      if (typeof Worker === "undefined") {
        this._diagnosticLog(DiagnosticLogEventIds.WorkerApiIsNotAvailable);
        return;
      }

      if (!this._healthConfig._offlineWorkerUrl) {
        this._diagnosticLog(DiagnosticLogEventIds.InvalidWorkerUrl);
        return;
      }

      this._diagnosticLog(DiagnosticLogEventIds.InstallingOfflineWorker);

      // Load our worker via a blob URL, so we can download and parse its full script outside of the main thread
      const workerBlob = new Blob(
        [
          `try{importScripts('${encodeURI(
            this._healthConfig._offlineWorkerUrl
          )}');}catch(e){self.postMessage({i:14,l:'W',r:e.toString()})}`
        ],
        {
          type: "application/javascript"
        }
      );
      this._offlineWorker = new Worker(URL.createObjectURL(workerBlob));

      // Log messages from the worker via ULS
      this._offlineWorker.onmessage = (event: MessageEvent) => {
        try {
          const loggingEvent: DiagnosticLogEvent = event.data;

          this._diagnosticLog(loggingEvent.i, loggingEvent.r);
        } catch (ex: any) {
          this._diagnosticLog(
            DiagnosticLogEventIds.FailedToProcessMessageFromOfflineWorker,
            ex.toString()
          );
        }
      };

      this._offlineWorker.postMessage(<OfflineEvent>{
        t: EventTypeForLoggerAndOfflineWorker.OfflineWorkerInit,
        v: this._healthConfig._absoluteTelemetryUrl
      });

      // Attempt to send any outstanding events from the previous session
      this._offlineWorker.postMessage(<OfflineEvent>{
        t: EventTypeForLoggerAndOfflineWorker.OfflineWorkerFlush
      });
    } catch (ex: any) {
      this._diagnosticLog(
        DiagnosticLogEventIds.FailedToInstallOfflineWorker,
        ex.toString()
      );
    }
  };

  private readonly _trySendPayloadToOfflineWorker = (
    payload: HealthRequestMinified,
    dimensionsVersion: number
  ): SendToOfflineWorkerResult => {
    // If the payload only contains heartbeats, then we don't need to send it via offline because heartbeats are
    // ignored in the offline scenario, since they are only a realtime concept.
    if (
      payload.b.length > 0 || // bSqm error ids
      payload.e.length > 0 || // QoS errors
      payload.q.length > 0 || // QoS pillars
      payload.k.length > 0 || // Kpis
      payload.l.length > 0 // Diagnostic logs
    ) {
      try {
        this._offlineWorker?.postMessage(<OfflineEvent>{
          t: EventTypeForLoggerAndOfflineWorker.OfflineWorkerFailedUpload,
          // Session_Id + dimensions version
          k: `${payload.d.w}.${dimensionsVersion}`,
          v: payload
        });
        return SendToOfflineWorkerResult.SendSucceeded;
      } catch (err: unknown) {
        const extraRuntimeInfo = this._convertExceptionToExtraRuntimeInfo(err);
        this._diagnosticLog(
          DiagnosticLogEventIds.FailedToSendEventToWorker,
          extraRuntimeInfo
        );
        return SendToOfflineWorkerResult.SendFailed;
      }
    }

    return SendToOfflineWorkerResult.DidNotAttempt;
  };

  // ------------------------------------------------------------------------
  // End: Offline Support (private)
  // ------------------------------------------------------------------------

  // ------------------------------------------------------------------------
  // Begin: Misc (private)
  // ------------------------------------------------------------------------

  private readonly _initKpiIfNeeded = (
    kpiName: string,
    kpiType?: KpiType,
    isAlertable?: boolean,
    owningFeatureCrewAlias?: string | nullable
  ) => {
    if (kpiName && kpiType !== undefined) {
      if (!this._kpis[kpiName]) {
        this._kpis[kpiName] = {
          k: kpiName,
          t: kpiType,
          a: isAlertable === true,
          o: owningFeatureCrewAlias ?? undefined
        };

        if (isAlertable) {
          this._alertableKpiNames.push(kpiName);
          this._logHeartbeatEvent();
        }
      }
    }
  };

  private readonly _pillarsChanged = (veryFirstPillar: boolean) => {
    // Reset ordering number to 0 to ensure that we always log this heartbeat - Word uses heartbeat numbers for sampling
    this._heartbeatOrderingNumber = -1;

    // If this is the first pillar being added, then start up the timer to record to the warm pipeline at a regular interval
    if (veryFirstPillar) {
      this._recordPillarLastTimestamp = Date.now();
      this._queueLogPillars();
      this._startHeartbeatIfNotAlreadyStarted();
    } else {
      this._logHeartbeatEvent();
    }
  };

  private readonly _checkForInitialization = (
    context?: InitializationCheckWarningContext
  ): boolean => {
    if (!this._initializationCompleted) {
      this._diagnosticLog(
        DiagnosticLogEventIds.AttemptToUseFunctionalityBeforeIitialization,
        context?.toString()
      );
      return false;
    }

    return true;
  };

  private readonly _tryUpload = (
    payload: HealthRequestMinified,
    dimensionsVersion: number,
    attemptsLeft: number
  ): void => {
    if (!payload) {
      return;
    }

    // Fetch the remote telemetry url now to save bytes later by having to reference "this."
    const remoteTelemetryUrl = this._healthConfig._telemetryUrl;

    // We should not attempt to send the payload if the remote telemetry url is empty.
    // Or, it would cause double-boot issue since the post URL would be WordEditorFrame.aspx
    if (remoteTelemetryUrl === "") {
      return;
    }

    // Store requests if
    // (1) we know a request has succeeded before AND
    //   (a) we exceeded our retry limit OR
    //   (b) we know that the app is no longer online
    // We only store failed requests after at least one request succeeds to avoid the case where
    // we start storing 100% of our events in IndexedDB due to our requests being blocked by an ad-blocker
    // We also choose to ignore a value of `true` in this._isAppOnline (aka the app *is* online) if a previous
    // request went through to avoid running into data loss due to a bug in our logic for detecting network changes
    if (
      this._hasAtLeastOneRequestSucceeded &&
      (attemptsLeft <= 0 || !this._isAppOnline)
    ) {
      const result = this._trySendPayloadToOfflineWorker(
        payload,
        dimensionsVersion
      );
      if (result !== SendToOfflineWorkerResult.SendFailed) {
        return;
      }
    }

    // We ran out of attempts and didn't get into the if-statement above allowing us to store the batch in IndexedDB
    if (attemptsLeft <= 0) {
      return;
    }

    // If we would have thrown just skip the log
    const stringifiedPayload = this._jsonStringifyNoThrow(payload, false);
    if (stringifiedPayload === null) return;

    if (this._isAppShuttingDown()) {
      // Attempt to use sendBeacon if it is defined. If anything goes wrong, fallback to async XHR
      try {
        if (typeof navigator.sendBeacon !== "undefined") {
          // Beacon API has a 64 KB payload limit if we are over it, the call will fail
          if (
            stringifiedPayload.length >= 64 * 1024 &&
            this._healthConfig._isErrorPartCEnabled
          ) {
            // Try trimming the payload by dropping the error Part C dimensions, if any were present
            const modifiedPayload = {
              ...payload,
              a: this._additionalPartCDimensions
            };

            // If we would have thrown just skip the log
            const modifiedStringPayload = this._jsonStringifyNoThrow(
              modifiedPayload,
              true
            );
            if (modifiedStringPayload === null) return;

            navigator.sendBeacon(remoteTelemetryUrl, modifiedStringPayload);
          } else {
            navigator.sendBeacon(remoteTelemetryUrl, stringifiedPayload);
          }

          return;
        }
      } catch (_: any) {}
    }

    // Keep this a simple request by not adding any custom headers. This avoids the preflight OPTIONS request.
    // CORS Simple Requests:https://developer.mozilla.org/en-US/docs/Web/HTTP/Access_control_CORS#Simple_requests
    const xhr = new XMLHttpRequest();
    xhr.open("POST", remoteTelemetryUrl, true /*async*/);

    xhr.onreadystatechange = () => {
      if (xhr.readyState === XMLHttpRequest.DONE) {
        if (xhr.status === 200) {
          // Now that a request has succeeded, we can start storing failed requests in IndexedDB
          if (!this._hasAtLeastOneRequestSucceeded) {
            this._hasAtLeastOneRequestSucceeded = true;
          }
        }
        // Attempt retrying the request if we can
        else if (
          this._currentRetriesCount < this._healthConfig._maxConcurrentRetries
        ) {
          this._currentRetriesCount += 1;

          window.setTimeout(() => {
            this._currentRetriesCount -= 1;
            this._tryUpload(payload, dimensionsVersion, attemptsLeft - 1);
          }, this._healthConfig._retryDelayMs);
        }
        // Send the payload to the offline worker if
        // (1) we're at our limit for concurrent retries AND
        // (2) we know a request has succeeded before
        else if (this._hasAtLeastOneRequestSucceeded) {
          // Ignoring result because we're already at the limit.
          this._trySendPayloadToOfflineWorker(payload, dimensionsVersion);
        }
      }
    };

    xhr.send(stringifiedPayload);
  };

  private readonly _diagnosticLog = (id: number, extraRuntimeInfo?: string) => {
    const minifiedEvent: { [name: string]: any } = {
      i: id,
      r: extraRuntimeInfo
    };

    this._loggerQueueEvent(
      EventTypeForLoggerAndOfflineWorker.DiagnosticLog,
      minifiedEvent,
      id.toString()
    );
  };

  private readonly _convertExceptionToExtraRuntimeInfo = (
    err: unknown
  ): string | undefined => {
    let extraRuntimeInfo: string | undefined = undefined;
    if (typeof err === "string") {
      extraRuntimeInfo = err;
    } else if (err instanceof Error) {
      extraRuntimeInfo = err.name + " " + err.message;
      if (err.stack) {
        extraRuntimeInfo += " " + err.stack;
      }
    }
    return extraRuntimeInfo;
  };

  private readonly _jsonStringifyNoThrow = (
    input: any,
    isModifiedayload: boolean
  ): string | null => {
    try {
      return jsonStringify(input);
    } catch (err: unknown) {
      let extraRuntimeInfo = this._convertExceptionToExtraRuntimeInfo(err);
      extraRuntimeInfo =
        "ModifiedPayload: " + isModifiedayload + " | " + extraRuntimeInfo;
      this._diagnosticLog(
        DiagnosticLogEventIds.FailedToJsonStringifyEvent,
        extraRuntimeInfo
      );
      return null;
    }
  };

  private readonly _tryUpload_fetchApi = (
    payload: HealthRequestMinified,
    dimensionsVersion: number,
    attemptsLeft: number
  ): undefined => {
    if (!payload) {
      return;
    }

    // Fetch the remote telemetry url now to save bytes later by having to reference "this."
    const remoteTelemetryUrl = this._healthConfig._telemetryUrl;

    // We should not attempt to send the payload if the remote telemetry url is empty.
    // Or, it would cause double-boot issue since the post URL would be WordEditorFrame.aspx
    if (remoteTelemetryUrl === "") {
      return;
    }

    // Store requests if
    // (1) we know a request has succeeded before AND
    //   (a) we exceeded our retry limit OR
    //   (b) we know that the app is no longer online
    // (2) This is not a headless session.
    // We only store failed requests after at least one request succeeds to avoid the case where
    // we start storing 100% of our events in IndexedDB due to our requests being blocked by an ad-blocker
    // We also choose to ignore a value of `true` in this._isAppOnline (aka the app *is* online) if a previous
    // request went through to avoid running into data loss due to a bug in our logic for detecting network changes
    if (
      this._hasAtLeastOneRequestSucceeded &&
      (attemptsLeft <= 0 || !this._isAppOnline)
    ) {
      const result = this._trySendPayloadToOfflineWorker(
        payload,
        dimensionsVersion
      );

      if (result !== SendToOfflineWorkerResult.SendFailed) {
        return;
      }

      // We ran out of attempts and didn't get into the if-statement above allowing us to store the batch in IndexedDB
      if (attemptsLeft <= 0) {
        return;
      }
    }

    // If we would have thrown just skip the log
    const stringifiedPayload = this._jsonStringifyNoThrow(payload, false);
    if (stringifiedPayload === null) return;

    if (this._isAppShuttingDown()) {
      // Attempt to send keepAlive, else send regular fetch request
      try {
        if (
          stringifiedPayload.length >= 64 * 1024 &&
          this._healthConfig._isErrorPartCEnabled
        ) {
          // Try trimming the payload by dropping the error Part C dimensions, if any were present
          const modifiedPayload = {
            ...payload,
            a: this._additionalPartCDimensions
          };

          // If we would have thrown just skip the log
          const modifiedStringPayload = this._jsonStringifyNoThrow(
            modifiedPayload,
            true
          );
          if (modifiedStringPayload === null) return;

          fetch(
            new Request(remoteTelemetryUrl, {
              method: "POST",
              body: modifiedStringPayload,
              keepalive: true
            })
          ).catch(() => {});
        } else {
          fetch(
            new Request(remoteTelemetryUrl, {
              method: "POST",
              body: stringifiedPayload,
              keepalive: true
            })
          ).catch(() => {});
        }
        return;
      } catch (_: any) {}
    }

    fetch(
      new Request(remoteTelemetryUrl, {
        method: "POST",
        body: stringifiedPayload
      })
    )
      .then((response) => {
        if (response.ok) {
          if (!this._hasAtLeastOneRequestSucceeded) {
            this._hasAtLeastOneRequestSucceeded = true;
          }
        } else if (
          this._currentRetriesCount < this._healthConfig._maxConcurrentRetries
        ) {
          this._currentRetriesCount += 1;

          globalThis.setTimeout(async () => {
            this._currentRetriesCount -= 1;
            this._tryUpload_fetchApi(
              payload,
              dimensionsVersion,
              attemptsLeft - 1
            );
          }, this._healthConfig._retryDelayMs);
        }
        // Send the payload to the offline worker if
        // (1) we're at our limit for concurrent retries AND
        // (2) we know a request has succeeded before
        else if (this._hasAtLeastOneRequestSucceeded) {
          // Ignoring result because we're already at the limit.
          this._trySendPayloadToOfflineWorker(payload, dimensionsVersion);
        }
      })
      .catch((_: any) => {});
  };
}

// ------------------------------------------------------------------------
// End: Misc (private)
// ------------------------------------------------------------------------

// This interface will be used internally to help save bytes,
// so that internally we don't have to keep using the publicly exposed HealthConfig
// property names which can't be minimized because they are part of our public api
interface HealthConfigInternal {
  _dimensions?: HealthDimensions; // dimensions
  _heartbeatIntervalMs: number; // heartbeatIntervalMs
  _isHealthEnabled: boolean; // isHealthEnabled
  _isOfflineEnabled: boolean; // isOfflineEnabled
  _isErrorPartCEnabled: boolean; // isErrorPartCEnabled
  _isFetchApiEnabled: boolean; // isFetchApiEnabled
  _maxIntervalMs: number; // maxIntervalMs
  _maxQueueSize: number; // maxQueueSize
  _offlineWorkerUrl: string; // offlineWorkerUrl
  _pillarIntervalMs: number; // pillarIntervalMs
  _telemetryUrl: string; // telemetryUrl
  _absoluteTelemetryUrl: string; // absoluteTelemetryUrl
  _maxConcurrentRetries: number; // maxConcurrentRetries
  _retryMaxAttempts: number; // retryMaxAttempts
  _retryDelayMs: number; // retryDelayMs
  _disabledHealthEvents: Set<string | nullable>; // disabledHealthEvents
}

function convertToHealthConfigInternal(
  healthConfig: HealthConfig
): HealthConfigInternal {
  return {
    _dimensions: healthConfig.dimensions ?? undefined,
    _heartbeatIntervalMs: healthConfig.heartbeatIntervalMs ?? 300000,
    _isHealthEnabled: healthConfig.isHealthEnabled ?? false,
    _isOfflineEnabled: healthConfig.isOfflineEnabled ?? false,
    _isErrorPartCEnabled: healthConfig.isErrorPartCEnabled ?? false,
    _isFetchApiEnabled: healthConfig.isFetchApiEnabled ?? false,
    _maxIntervalMs: healthConfig.loggerMaxIntervalMs ?? 30000,
    _maxQueueSize: healthConfig.loggerMaxQueueSize ?? 25,
    _offlineWorkerUrl: healthConfig.offlineWorkerUrl ?? "",
    _absoluteTelemetryUrl:
      healthConfig.absoluteRemoteTelemetryUrlForOfflineWorker ?? "",
    _pillarIntervalMs:
      healthConfig.recordPillarToWarmPipelineIntervalMs ?? 86400000,
    _telemetryUrl: healthConfig.remoteTelemetryUrl ?? "",
    _maxConcurrentRetries: healthConfig.maxConcurrentRetries ?? 3,
    _retryMaxAttempts: healthConfig.retryMaxAttempts ?? 3,
    _retryDelayMs: healthConfig.retryDelayMs ?? 15000,
    _disabledHealthEvents:
      healthConfig.disabledHealthEvents ?? new Set<string>()
  };
}
